import StepHtmlActivityBubble, {
  type StepHtmlActivityBubbleHandle,
} from "../ui/StepHtmlActivityBubble";

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  AbcGameArtifactConfig,
  CopingBuddyArtifactConfig,
  CopingBuddySkillConfig,
  HtmlActivityArtifactConfig,
  TargetGameArtifactConfig,
} from "../../types/chat";

import { N8N_ABC_GAME_URL } from "../../config/n8n";

import CoachSkillWidget, {
  type CoachSkillWidgetHandle,
} from "../coachSkills/CoachSkillWidget";

import StepAbcGameBubble, {
  type StepAbcGameBubbleHandle,
} from "../ui/StepAbcGameBubble";

import StepTargetGameBubble, {
  type StepTargetGameBubbleHandle,
} from "../targetGame/StepTargetGameBubble";

type CoachSkillActivityEvent = {
  widgetType: "coping_buddy" | "learning_buddy";
  widgetLabel: "Coping Buddy" | "Learning Buddy";
  activityKey: string;
  activityTitle: string;
  eventType: "started" | "completed" | "used" | "saved" | "submitted";
  metadata?: Record<string, unknown>;
};

type Props = {
  artifact: CopingBuddyArtifactConfig;

  onActivityEvent?: (
    event: CoachSkillActivityEvent
  ) => void;

  onAudioResponse?: (
    audioBase64?: string | null,
    mimeType?: string | null,
    audioUrl?: string | null
  ) => void | Promise<void>;

  onHtmlActivityExpandedChange?: (
    expanded: boolean
  ) => void;

  /**
 * Pass the authoritative ABC Game "Buddy says" text
 * upward to ChatContainer.
 */
  onAbcGameMessage?: (
    message: string
  ) => void;

  onTargetGameMessage?: (
    message: string
  ) => void;

  gptLiveActive?: boolean;

};

export type StepCopingBuddyBubbleHandle = {

  // Forward commands to an active nested Coach Skill.
  executeActiveCoachSkillVoiceCommand: (
    raw: string
  ) => boolean;

  executeActiveHtmlActivityVoiceCommand: (
    raw: string
  ) => boolean;

  executeActiveTargetGameVoiceCommand: (
    raw: string
  ) => boolean;

  /**
   * Attempt to perform one of the currently available Coping Buddy
   * card selections from a spoken command.
   *
   * Returns the selected card title when handled.
   * Returns null when the spoken text does not exactly match
   * a supported current card action.
   */
  executeVoiceCommand: (
    raw: string
  ) => string | null;

  /**
   * Give the currently active embedded coping activity
   * the first opportunity to consume a realtime-voice transcript.
   *
   * Returns true when the active child owns/consumes the utterance.
   * Returns false when there is no supported active child or
   * when the child has ended and normal chat can regain voice.
   */
  executeActiveActivityVoiceCommand: (
    raw: string
  ) => boolean;
};

function titleCase(value: string): string {
  if (!value) return "";

  return value.charAt(0).toUpperCase() + value.slice(1);
}

function normalizeVoiceChoice(
  value: string
): string {
  return String(value || "")
    .normalize("NFKC")
    .toLowerCase()

    // GPT-Live may include nonverbal transcript annotations.
    // They are not part of the user's intended UI command.
    .replace(
      /\[(?:inhale|exhale|breath|breathing|sigh|pause)\]/gi,
      " "
    )

    .replace(/[’]/g, "'")
    .replace(/[^a-z0-9\s']/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const StepCopingBuddyBubble =
  forwardRef<
    StepCopingBuddyBubbleHandle,
    Props
  >(
    (
      {
        artifact,
        onActivityEvent,
        onAudioResponse,
        onHtmlActivityExpandedChange,
        onAbcGameMessage,
        onTargetGameMessage,
        gptLiveActive = false,
      },
      ref
    ) => {
      // ------------------------------------------------------------
      // Available coping skills
      // ------------------------------------------------------------

      const skills = useMemo(() => {
        return Array.isArray(artifact.skills)
          ? artifact.skills.filter(
            (skill) => skill.enabled !== false
          )
          : [];
      }, [artifact.skills]);

      const [selectedSkillId, setSelectedSkillId] =
        useState<string | null>(null);

      // Scroll target for the currently opened coping activity.
      const selectedSkillContentRef =
        useRef<HTMLDivElement | null>(null);

      // Bring the selected activity into view after React renders it.
      // Works for both mouse selection and GPT-Live voice selection.
      useEffect(() => {
        if (!selectedSkillId) return;

        const frame = requestAnimationFrame(() => {
          selectedSkillContentRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        });

        return () => cancelAnimationFrame(frame);
      }, [selectedSkillId]);

      const selectSkill = useCallback(
        (skillId: string) => {
          setSelectedSkillId(skillId);
        },
        []
      );

      // ------------------------------------------------------------
      // Active embedded ABC Game
      // ------------------------------------------------------------
      //
      // When ABC Game is selected, realtime voice can be routed
      // directly into the game instead of normal chat.
      const abcGameRef =
        useRef<StepAbcGameBubbleHandle | null>(
          null
        );

      const coachSkillRef =
        useRef<CoachSkillWidgetHandle | null>(null);

      const htmlActivityRef =
        useRef<StepHtmlActivityBubbleHandle | null>(null);

      const targetGameRef =
        useRef<StepTargetGameBubbleHandle | null>(null);

      useImperativeHandle(
        ref,
        () => ({
          // --------------------------------------------------------
          // COPING BUDDY CARD SELECTION
          // --------------------------------------------------------
          executeVoiceCommand: (
            raw: string
          ): string | null => {
            const normalized =
              normalizeVoiceChoice(raw);

            if (!normalized) {
              return null;
            }

            // Strip only known UI-selection prefixes.
            //
            // The remaining text must STILL exactly match a supported
            // card name/alias, so this does not introduce fuzzy matching.
            //
            // Examples:
            //   "Let's try hills and valleys"
            //       -> "hills and valleys"
            //
            //   "Can we try ABC game"
            //       -> "abc game"
            //
            //   "Let's try hills and valleys because I'm anxious"
            //       -> "hills and valleys because i'm anxious"
            //       -> NO MATCH
            let normalizedChoice = normalized;

            const selectionPrefixes = [
              "i want to try ",
              "i want to do ",
              "please choose ",
              "please select ",
              "please pick ",
              "please try ",

              "can we try ",
              "can i try ",
              "can we do ",
              "can i do ",

              "let's try ",
              "lets try ",
              "let's pick ",
              "lets pick ",
              "let's do ",
              "lets do ",

              "i want ",
              "i pick ",

              "choose ",
              "select ",
              "pick ",
              "try ",
              "start ",
              "do ",
            ];

            for (const prefix of selectionPrefixes) {
              if (
                normalizedChoice.startsWith(prefix)
              ) {
                normalizedChoice =
                  normalizedChoice
                    .slice(prefix.length)
                    .trim();

                break;
              }
            }

            for (const skill of skills) {
              const title =
                String(skill.title || "").trim();

              if (!title) continue;

              const normalizedTitle =
                normalizeVoiceChoice(title);

              const aliases =
                new Set<string>();

              // --------------------------------------------------
              // Add one spoken name plus its safe selection phrases.
              // --------------------------------------------------
              const addAliasFamily = (
                rawAlias: string
              ) => {
                const alias =
                  normalizeVoiceChoice(rawAlias);

                if (!alias) return;

                [
                  alias,

                  `do ${alias}`,
                  `start ${alias}`,
                  `choose ${alias}`,
                  `select ${alias}`,
                  `pick ${alias}`,
                  `try ${alias}`,

                  `i want ${alias}`,
                  `i want to do ${alias}`,
                  `i want to try ${alias}`,
                  `i pick ${alias}`,

                  `lets do ${alias}`,
                  `let's do ${alias}`,

                  `lets pick ${alias}`,
                  `let's pick ${alias}`,

                  `lets try ${alias}`,
                  `let's try ${alias}`,

                  `can we do ${alias}`,
                  `can i do ${alias}`,
                  `can we try ${alias}`,
                  `can i try ${alias}`,

                  `please choose ${alias}`,
                  `please pick ${alias}`,
                  `please try ${alias}`,
                ].forEach((value) => {
                  aliases.add(value);
                });
              };

              // Always support the exact visible card title.
              addAliasFamily(
                normalizedTitle
              );

              // --------------------------------------------------
              // Known speech-recognition / natural-language aliases.
              //
              // Keep this explicit and deterministic.
              // Do NOT use fuzzy matching.
              // --------------------------------------------------
              switch (normalizedTitle) {
                case "box breathing":
                  addAliasFamily(
                    "box breath"
                  );
                  break;

                case "hills and valleys":
                  addAliasFamily(
                    "hill and valley"
                  );

                  addAliasFamily(
                    "hills and valley"
                  );

                  // Observed GPT-Live transcription variant.
                  addAliasFamily(
                    "skills and valleys"
                  );

                  break;

                case "leaves on a stream":
                  addAliasFamily(
                    "leaves on stream"
                  );

                  addAliasFamily(
                    "leaf on a stream"
                  );

                  break;

                case "grounding room":
                  addAliasFamily(
                    "the grounding room"
                  );
                  break;

                case "erp sketcher":
                  addAliasFamily(
                    "erp sketch"
                  );

                  addAliasFamily(
                    "e r p sketcher"
                  );

                  addAliasFamily(
                    "e r p sketch"
                  );

                  break;

                case "pmr for kids":
                  addAliasFamily(
                    "p m r for kids"
                  );

                  addAliasFamily(
                    "pmr"
                  );

                  addAliasFamily(
                    "p m r"
                  );

                  addAliasFamily(
                    "progressive muscle relaxation"
                  );

                  addAliasFamily(
                    "progressive muscle relaxation for kids"
                  );

                  break;

                case "butterfly breath":
                  addAliasFamily(
                    "butterfly breathing"
                  );
                  break;

                case "five senses grounding":
                  addAliasFamily(
                    "5 senses grounding"
                  );

                  addAliasFamily(
                    "five senses"
                  );

                  addAliasFamily(
                    "5 senses"
                  );

                  break;

                case "abc game":
                  addAliasFamily(
                    "a b c game"
                  );

                  addAliasFamily(
                    "abc"
                  );

                  addAliasFamily(
                    "a b c"
                  );

                  break;

                case "target game":
                  // Exact title should normally be sufficient.
                  break;
              }

              if (
                !aliases.has(normalized) &&
                !aliases.has(normalizedChoice)
              ) {
                continue;
              }

              selectSkill(skill.id);

              console.info(
                "[Coping Buddy][Voice] selected",
                {
                  transcript: raw,
                  skillId: skill.id,
                  title,
                }
              );

              return title;
            }

            return null;
          },

          // --------------------------------------------------------
          // ACTIVE NESTED COACH SKILL
          // --------------------------------------------------------
          executeActiveCoachSkillVoiceCommand: (
            raw: string
          ): boolean => {
            const activeSkill =
              skills.find(
                (skill) => skill.id === selectedSkillId
              ) ?? null;

            if (
              activeSkill?.renderType !== "coachSkill" ||
              !activeSkill.coachSkill
            ) {
              return false;
            }

            return (
              coachSkillRef.current?.executeVoiceCommand(raw) ===
              true
            );
          },


          // --------------------------------------------------------
          // ACTIVE HTML ACTIVITY VOICE CONTROLS
          // --------------------------------------------------------
          executeActiveHtmlActivityVoiceCommand: (
            raw: string
          ): boolean => {
          
            const activeSkill =
              skills.find(
                (skill) => skill.id === selectedSkillId
              ) ?? null;

            const activityKey = String(
              activeSkill?.activityKey ||
              activeSkill?.id ||
              ""
            ).trim().toLowerCase();

            if (
              activeSkill?.renderType !== "htmlActivity" ||
              activityKey !== "butterfly_breath"
            ) {
              return false;
            }

            return (
              htmlActivityRef.current
                ?.executeVoiceCommand(raw) === true
            );
          },


          // --------------------------------------------------------
          // ACTIVE TARGET GAME VOICE COMMANDS
          // --------------------------------------------------------
          executeActiveTargetGameVoiceCommand: (
            raw: string
          ): boolean => {
            const transcript = String(raw || "").trim();
            if (!transcript) return false;

            const activeSkill =
              skills.find(
                (skill) => skill.id === selectedSkillId
              ) ?? null;

            if (activeSkill?.artifactType !== "target_game") {
              return false;
            }

            const targetGame = targetGameRef.current;

            // Target Game is selected, but React may
            // still be mounting the game component.
            // Keep the utterance from leaking into normal chat.
            if (!targetGame) {
              console.info(
                "[Coping Buddy][Target Game] selected but ref not ready",
                { transcript }
              );
              return true;
            }

            const handled =
              targetGame.executeVoiceCommand(transcript);

            console.info(
              "[Coping Buddy][Target Game] voice ownership",
              {
                transcript,
                handled,
              }
            );

            return handled;
          },


          // --------------------------------------------------------
          // ACTIVE EMBEDDED COPING ACTIVITY
          // --------------------------------------------------------
          executeActiveActivityVoiceCommand: (
            raw: string
          ): boolean => {
            const transcript =
              String(raw || "").trim();

            if (!transcript) {
              return false;
            }

            const activeSkill =
              skills.find(
                (skill) =>
                  skill.id === selectedSkillId
              ) ?? null;

            // For now, only ABC Game takes ownership
            // of the always-on realtime voice stream.
            if (
              activeSkill?.artifactType !==
              "abc_game"
            ) {
              return false;
            }

            const abcGame =
              abcGameRef.current;

            // ABC Game was selected, but React may still be
            // mounting the child component.
            //
            // Consume the utterance during that short window
            // so it does not leak into normal chat.
            if (!abcGame) {
              console.info(
                "[Coping Buddy][Realtime Voice] ABC Game selected but ref not ready",
                {
                  transcript,
                }
              );

              return true;
            }

            const handled =
              abcGame.executeVoiceCommand(
                transcript
              );

            if (handled) {
              console.info(
                "[Coping Buddy][Realtime Voice] ABC Game owns transcript",
                {
                  transcript,
                }
              );
            } else {
              console.info(
                "[Coping Buddy][Realtime Voice] ABC Game released voice ownership",
                {
                  transcript,
                }
              );
            }

            return handled;
          },
        }),
        [
          skills,
          selectedSkillId,
          selectSkill,
        ]
      );

      const selectedSkill: CopingBuddySkillConfig | null =
        useMemo(() => {
          return (
            skills.find(
              (skill) => skill.id === selectedSkillId
            ) ?? null
          );
        }, [skills, selectedSkillId]);

      // ------------------------------------------------------------
      // ABC Game artifact
      // ------------------------------------------------------------

      const abcGameArtifact: AbcGameArtifactConfig | null =
        useMemo(() => {
          if (selectedSkill?.artifactType !== "abc_game") {
            return null;
          }

          const parentContext = artifact.context ?? {};

          const sessionId =
            parentContext.sessionId ?? null;

          const accessCode =
            parentContext.accessCode ?? null;

          const patientId =
            parentContext.patientId ?? null;

          const responseType =
            (parentContext as any).responseType ??
            (parentContext as any).outputType ??
            (parentContext as any).audioType ??
            (parentContext as any).type ??
            "text";

          const voiceId =
            (parentContext as any).voiceId ?? null;

          const inputMode =
            (parentContext as any).inputMode ?? null;

          const dictationOrigin =
            (parentContext as any).dictationOrigin ??
            false;

          const keyBase =
            sessionId ||
            accessCode ||
            "coping-buddy";

          return {
            id: `abc-game-${keyBase}`,
            stepId: "0",

            label:
              selectedSkill.title ||
              "ABC Game",

            hint:
              selectedSkill.subtitle ||
              "Practice naming foods or animals in alphabetical order.",

            type: "abc_game",

            title:
              selectedSkill.title ||
              "ABC Game",

            key: `abcGame:${keyBase}`,

            context: {
              ...parentContext,

              sessionId,
              accessCode,
              patientId,

              responseType,
              voiceId,
              inputMode,
              dictationOrigin,
            },

            mode: "practice",

            allowRestart: true,
            allowQuit: true,

            api: {
              baseUrl: N8N_ABC_GAME_URL,
            },
          };
        }, [
          artifact.context,
          selectedSkill,
        ]);

      // ------------------------------------------------------------
      // Target Game artifact
      // ------------------------------------------------------------

      const targetGameArtifact:
        TargetGameArtifactConfig | null =
        useMemo(() => {
          if (
            selectedSkill?.artifactType !==
            "target_game"
          ) {
            return null;
          }

          const parentContext =
            artifact.context ?? {};

          const sessionId =
            parentContext.sessionId ?? null;

          const accessCode =
            parentContext.accessCode ?? null;

          const patientId =
            parentContext.patientId ?? null;

          const responseType =
            (parentContext as any).responseType ??
            (parentContext as any).outputType ??
            (parentContext as any).audioType ??
            (parentContext as any).type ??
            "text";

          const voiceId =
            (parentContext as any).voiceId ??
            null;

          const inputMode =
            (parentContext as any).inputMode ??
            null;

          const dictationOrigin =
            (parentContext as any)
              .dictationOrigin ?? false;

          const keyBase =
            sessionId ||
            accessCode ||
            "coping-buddy";

          return {
            id: `target-game-${keyBase}`,
            stepId: "0",

            label:
              selectedSkill.title ||
              "Target Game",

            hint:
              selectedSkill.subtitle ||
              "Ask yes or no questions to narrow down a mystery item.",

            type: "target_game",

            title:
              selectedSkill.title ||
              "Target Game",

            key: `targetGame:${keyBase}`,

            context: {
              ...parentContext,

              sessionId,
              accessCode,
              patientId,

              responseType,
              voiceId,
              inputMode,
              dictationOrigin,
            },

            mode: "practice",

            config:
              (
                selectedSkill.config as
                TargetGameArtifactConfig["config"]
              ) || {
                store: "Target",

                goal:
                  "Guess the item",

                instructions:
                  "In the Target Game, Buddy secretly chooses an item from Target. Ask yes or no questions until you can identify it.",

                allowRestart: true,
                allowHints: true,
              },
          };
        }, [
          artifact.context,
          selectedSkill,
        ]);

      // ------------------------------------------------------------
      // HTML activity artifact
      // ------------------------------------------------------------

      const htmlActivityArtifact:
        HtmlActivityArtifactConfig | null =
        useMemo(() => {
          if (
            selectedSkill?.renderType !==
            "htmlActivity"
          ) {
            return null;
          }

          const sessionId =
            artifact.context?.sessionId ??
            null;

          const accessCode =
            artifact.context?.accessCode ??
            null;

          const keyBase =
            sessionId ||
            accessCode ||
            "coping-buddy";

          const activityKey =
            selectedSkill.activityKey ||
            selectedSkill.id;

          const configuredUrl =
            (
              selectedSkill.config as
              | { url?: unknown }
              | undefined
            )?.url;

          return {
            id: `${selectedSkill.id}-${keyBase}`,

            stepId: "0",

            label:
              selectedSkill.title ||
              "Activity",

            hint:
              selectedSkill.subtitle ||
              "",

            type: "html_activity",

            activityKey,

            url:
              typeof configuredUrl === "string"
                ? configuredUrl
                : `/activities/${activityKey}.html`,
          };
        }, [
          artifact.context,
          selectedSkill,
        ]);

      // ------------------------------------------------------------
      // Render
      // ------------------------------------------------------------

      return (
        <div className="mt-2 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
          {/* Header */}
          <div className="bg-gradient-to-br from-indigo-700 via-indigo-500 to-indigo-400 px-7 py-7 text-white shadow-sm sm:px-8 sm:py-8">
            <div className="text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              Coping Skills Buddy
            </div>

            <div className="mt-3 max-w-3xl text-base font-medium leading-7 text-white/95">
              A simple, supportive practice tool for
              distraction-based coping skills. Choose a
              coping skill, follow the prompts, and focus
              on one small step at a time.
            </div>
          </div>

          <div className="p-4">
            {skills.length > 0 ? (
              <>
                {/* Skill cards */}
                <div className="grid gap-3 sm:grid-cols-2">
                  {skills.map((skill) => {
                    const isSelected =
                      skill.id === selectedSkillId;

                    return (
                      <button
                        key={skill.id}
                        type="button"
                        onClick={() => {
                          selectSkill(skill.id);
                        }}
                        className={[
                          "rounded-2xl border p-4 text-left transition",

                          "focus:outline-none focus:ring-4 focus:ring-indigo-100",

                          isSelected
                            ? "border-indigo-300 bg-indigo-50 shadow-sm"
                            : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white",
                        ].join(" ")}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              {skill.title}
                            </div>

                            {skill.subtitle ? (
                              <div className="mt-1 text-sm text-slate-600">
                                {skill.subtitle}
                              </div>
                            ) : null}
                          </div>

                          {isSelected ? (
                            <div className="shrink-0 rounded-full bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white">
                              Selected
                            </div>
                          ) : null}
                        </div>

                        {skill.category ? (
                          <div className="mt-3 inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
                            {titleCase(
                              skill.category
                            )}
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                {/* Selected skill */}
                <div
                  ref={selectedSkillContentRef}
                  className="mt-4 scroll-mt-3"
                >
                  {selectedSkill?.renderType ===
                    "coachSkill" &&
                    selectedSkill.coachSkill ? (
                    <CoachSkillWidget
                      ref={coachSkillRef}
                      coachSkill={selectedSkill.coachSkill}
                      onActivityEvent={onActivityEvent}
                    />
                  ) : selectedSkill?.artifactType ===
                    "abc_game" &&
                    abcGameArtifact ? (
                    <StepAbcGameBubble
                      ref={abcGameRef}
                      artifact={abcGameArtifact}
                      onAudioResponse={
                        onAudioResponse
                      }
                      onGameMessage={
                        onAbcGameMessage
                      }
                    />
                  ) : selectedSkill?.artifactType ===
                    "target_game" &&
                    targetGameArtifact ? (
                    <StepTargetGameBubble
                      ref={targetGameRef}
                      artifact={targetGameArtifact}
                      onAudioResponse={onAudioResponse}
                      onGameMessage={onTargetGameMessage}
                      gptLiveActive={gptLiveActive}
                    />
                  ) : selectedSkill?.renderType ===
                    "htmlActivity" &&
                    htmlActivityArtifact ? (
                    <StepHtmlActivityBubble
                      ref={htmlActivityRef}
                      artifact={
                        htmlActivityArtifact
                      }
                      sessionId={
                        artifact.context
                          ?.sessionId ||
                        artifact.context
                          ?.accessCode ||
                        "coping-buddy"
                      }
                      accessCode={
                        artifact.context
                          ?.accessCode ||
                        undefined
                      }
                      onFinished={() => { }}
                      onActivityEvent={
                        onActivityEvent
                      }
                      onExpandedChange={
                        onHtmlActivityExpandedChange
                      }
                    />
                  ) : selectedSkill ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                      This coping skill is not
                      available yet.
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Select any card above to
                      begin.
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                No coping skills are available
                yet.
              </div>
            )}
          </div>
        </div>
      );
    }
  );

StepCopingBuddyBubble.displayName =
  "StepCopingBuddyBubble";

export default StepCopingBuddyBubble;